import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AssetNameSchema } from 'app/lib/schemas';
import { useSelector, useDispatch } from 'app/lib/hooks';
import { Button, ButtonSize } from 'app/components/core/Button';
import { Form, Field, Submit, Yup, useFormContext } from 'app/components/core/Form';
import { Input } from 'app/components/core/Input';
import {
    doCreateProject,
    selectIsCreatingProject,
    selectObjectToImportToCreatedProject,
    doCloseCreateProjectDialog,
    selectIsAddingExplorations
} from 'app/store/projects';
import {
    useDialogContext,
    DialogChapter,
    DialogChapterLayout,
    DialogActions,
    KeyValueSummary
} from 'app/components/features/DialogWithChapters';
import { ProjectEnvironmentChapter } from 'app/components/composite/ProjectEnvironmentChapter';

const schema = Yup.object({
    name: AssetNameSchema.concat(
        Yup.string().label('Project Name').meta({ summary: true })
    ).required(),
    environmentUid: Yup.string().label('Project Environment').meta({ summary: true })
}).label('New Project');

const initialValues = {
    name: ''
};

function ProjectDetailsChapter() {
    const { t } = useTranslation();
    const { getFieldValue } = useFormContext();
    const name = getFieldValue('name');

    const summary = useMemo(() => {
        const values = {
            [t('common:name')]: name
        };
        return <KeyValueSummary values={values} />;
    }, [t, name]);

    return (
        <DialogChapter
            label={t('components:CreateProjectEnv.projectDetails')}
            summary={summary}
            defaultOpen>
            <Field component={Input} name="name" label={t('common:name')} autoFocus />
        </DialogChapter>
    );
}

export function CreateProjectForm() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isCreatingProject = useSelector(selectIsCreatingProject);
    const addressToImport = useSelector(selectObjectToImportToCreatedProject);
    const isAddingExplorations = useSelector(selectIsAddingExplorations);

    const { onUpdateProjectSummary } = useDialogContext();
    const handleSubmit = useCallback(
        ({ name, environmentUid }) => {
            dispatch(
                doCreateProject({
                    name,
                    addressToImport,
                    isAddingExplorations,
                    envUid: environmentUid
                })
            );
        },
        [addressToImport, isAddingExplorations, dispatch]
    );

    const handleClose = useCallback(() => {
        dispatch(doCloseCreateProjectDialog());
    }, [dispatch]);

    return (
        <Form
            schema={schema}
            initialValues={initialValues}
            onSubmit={handleSubmit}
            isSubmitting={isCreatingProject}
            onUpdateSummary={onUpdateProjectSummary}
            allowTransformation>
            <DialogChapterLayout id="create-project-form">
                <ProjectDetailsChapter />
                <ProjectEnvironmentChapter
                    isOptional
                    hint={t('components:CreateProjectEnv.envHint')}
                />
                <DialogActions>
                    <Button onClick={handleClose} size={ButtonSize.MEDIUM}>
                        {t('common:cancel')}
                    </Button>
                    <Submit size={ButtonSize.MEDIUM}>{t('common:ok')}</Submit>
                </DialogActions>
            </DialogChapterLayout>
        </Form>
    );
}
